import React, { useMemo, memo, useState, useRef, useEffect, useCallback } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import DashboardComponentRenderer from './DashboardComponentRenderer';
import { calculateRowHeight } from '../utils/dashboardStyleUtils';
import ContextMenu from './ContextMenu';
import GroupBoundingBox from './GroupBoundingBox';
import { getGroups, getGroupComponentIds } from '../utils/groupUtils';

const ResponsiveGridLayout = WidthProvider(Responsive);

/**
 * DashboardGrid 컴포넌트
 * react-grid-layout을 사용하여 대시보드 그리드를 렌더링합니다.
 * 
 * ===== 팝업 자동 크기 모드 지원 =====
 * overrideRowHeight prop이 전달되면:
 *   - 기본 rowHeight 계산 로직을 무시하고 전달받은 값을 사용
 *   - 정사각형 그리드 구현 (1칸 너비 = 1칸 높이)
 *   - 차트 비율이 왜곡되지 않고 원래 모양 유지
 */
const DashboardGrid = memo(({
  dashboardTemplate,
  layouts,
  editMode,
  validComponentsArray,
  chartsData,
  componentResizeCounters,
  compactType,
  getEffectiveCompactType,
  handleLayoutChange,
  handleBreakpointChange,
  handleDragStart,
  handleDrag,
  handleDragStop,
  handleResizeStart,
  handleResize,
  handleResizeStop,
  handleComponentDoubleClick,
  deleteComponent,
  refreshChartData,
  refreshAllChartsData,
  triggerComponentResize,
  isUndoRedoOperation, // 🔄 undo/redo 작업 중 플래그 추가
  isChatbotAddingComponent, // 챗봇 컴포넌트 추가 중 플래그
  textLabelPreviewData, // 텍스트 라벨 실시간 미리보기 데이터
  setIsDragging: setParentIsDragging, // 부모 컴포넌트의 드래그 상태 설정 함수
  onChartClick, // 차트 클릭 이벤트 핸들러
  activeChartState, // 클릭된 차트 상태
  clickedChartIds, // 현재 클릭된 차트 IDs (독립선택 그룹 지원)
  selectedComponentIds, // 선택된 컴포넌트 IDs (다중 선택)
  isComponentSelected, // 컴포넌트 선택 여부 확인 함수
  onComponentSelect, // 컴포넌트 mouseDown 핸들러
  onComponentClick, // 컴포넌트 클릭 핸들러
  onCreateGroup, // 그룹 생성 핸들러
  onUngroupComponents, // 그룹 해제 핸들러
  onToggleGroupLock, // 그룹 잠금/해제 핸들러
  onToggleGroupIsolation, // 그룹 독립선택 토글 핸들러
  onToggleChartIsolation, // 개별 차트 독립선택 토글 핸들러
  onSelectGroupComponents, // 그룹 컴포넌트 선택 핸들러
  getComponentGroup, // 컴포넌트의 그룹 ID 가져오기
  onGroupResize, // 그룹 리사이즈 핸들러
  onGroupDrag, // 그룹 드래그 핸들러
  printMode = false,
  currentPopupId = null, // 현재 팝업 ID (중첩 팝업 지원)
  overrideRowHeight = null // 팝업 자동 크기: 오버라이드 행 높이
}) => {
  // 모든 Hook을 함수 시작 직후에 선언
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, targetComponentId: null });
  const [isDragging, setIsDragging] = useState(false);
  const [draggedComponentId, setDraggedComponentId] = useState(null);
  const [isCtrlPressed, setIsCtrlPressed] = useState(false);
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const autoScrollIntervalRef = useRef(null);
  const dashboardContainerRef = useRef(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // 레이아웃 안정화 추적을 위한 ref들
  const lastLayoutRef = useRef(null);
  const layoutChangeCountRef = useRef(0);
  const isStableRef = useRef(false);

  // 대시보드 템플릿이 변경될 때마다 레이아웃 추적 리셋
  useEffect(() => {
    lastLayoutRef.current = null;
    layoutChangeCountRef.current = 0;
    isStableRef.current = false;
  }, [dashboardTemplate?.id, dashboardTemplate?.dashboard_id]);

  // 자동 스크롤을 위한 ref와 state

  // 컨테이너 폭 측정을 위한 ref와 state

  // 자동 스크롤 기능
  const handleAutoScroll = useCallback((clientY) => {
    if (!isDragging) return;
    
    // 스크롤 컨테이너 찾기 - 캐시된 참조 사용
    let scrollContainer = dashboardContainerRef.current;
    if (!scrollContainer) {
      scrollContainer = containerRef.current?.closest('.dashboard-container');
    }
    if (!scrollContainer) {
      scrollContainer = document.body;
    }
    if (!scrollContainer) {
      scrollContainer = document.documentElement;
    }
    
    const containerRect = scrollContainer.getBoundingClientRect ? 
      scrollContainer.getBoundingClientRect() : 
      { top: 0, bottom: window.innerHeight };
    
    const scrollZoneSize = 120; // 스크롤 영역 크기
    const scrollSpeed = 60; // 스크롤 속도 조정 (80 → 60)
    
    // 자동 스크롤 영역 체크
    const distanceFromTop = clientY - containerRect.top;
    const distanceFromBottom = containerRect.bottom - clientY;
    
    // 기존 인터벌 정리
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
    
    // 상단 스크롤 영역
    if (distanceFromTop < scrollZoneSize && distanceFromTop > 0) {
      const scrollIntensity = Math.max(0.5, (scrollZoneSize - distanceFromTop) / scrollZoneSize);
      
      autoScrollIntervalRef.current = setInterval(() => {
        const currentScroll = scrollContainer.scrollTop || window.pageYOffset || document.documentElement.scrollTop;
        if (currentScroll > 0) {
          const scrollAmount = scrollSpeed * scrollIntensity;
          
          // 스크롤 시도 (GPU 가속 사용)
          if (scrollContainer.scrollTop !== undefined) {
            scrollContainer.scrollTop -= scrollAmount;
          }
          if (scrollContainer === document.body || scrollContainer === document.documentElement) {
            window.scrollBy({ top: -scrollAmount, behavior: 'auto' });
          }
        }
      }, 16); // 60fps로 최적화 (6ms → 16ms)
    }
    // 하단 스크롤 영역
    else if (distanceFromBottom < scrollZoneSize && distanceFromBottom > 0) {
      const scrollIntensity = Math.max(0.5, (scrollZoneSize - distanceFromBottom) / scrollZoneSize);
      
      autoScrollIntervalRef.current = setInterval(() => {
        const maxScroll = Math.max(
          scrollContainer.scrollHeight - scrollContainer.clientHeight,
          document.documentElement.scrollHeight - window.innerHeight
        );
        const currentScroll = scrollContainer.scrollTop || window.pageYOffset || document.documentElement.scrollTop;
        
        if (currentScroll < maxScroll) {
          const scrollAmount = scrollSpeed * scrollIntensity;
          
          // 스크롤 시도 (GPU 가속 사용)
          if (scrollContainer.scrollTop !== undefined) {
            scrollContainer.scrollTop += scrollAmount;
          }
          if (scrollContainer === document.body || scrollContainer === document.documentElement) {
            window.scrollBy({ top: scrollAmount, behavior: 'auto' });
          }
        }
      }, 16); // 60fps로 최적화 (6ms → 16ms)
    }
  }, [isDragging]);
  
  // 자동 스크롤 중지
  const stopAutoScroll = useCallback(() => {
    if (autoScrollIntervalRef.current) {
      clearInterval(autoScrollIntervalRef.current);
      autoScrollIntervalRef.current = null;
    }
  }, []);
  
  // 컴포넌트 언마운트 시 정리
  useEffect(() => {
    return () => {
      stopAutoScroll();
    };
  }, [stopAutoScroll]);
  
  // Ctrl/Cmd/Shift 키 상태 추적
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        setIsCtrlPressed(true);
      }
      if (e.shiftKey) {
        setIsShiftPressed(true);
      }
    };
    
    const handleKeyUp = (e) => {
      if (!e.ctrlKey && !e.metaKey) {
        setIsCtrlPressed(false);
      }
      if (!e.shiftKey) {
        setIsShiftPressed(false);
      }
    };
    
    const handleBlur = () => {
      // 포커스를 잃으면 Ctrl/Shift 상태 초기화
      setIsCtrlPressed(false);
      setIsShiftPressed(false);
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);
  
  // 컨테이너 폭 측정 - passive 이벤트 사용
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth);
      }
    };
    
    updateWidth();
    window.addEventListener('resize', updateWidth, { passive: true });
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // 컨테이너 자체 리사이즈에 반응 (우측 패널 드래그 등 윈도우 리사이즈가 아닌 변화를 포착)
  useEffect(() => {
    try {
      if (!containerRef.current || typeof window === 'undefined' || !('ResizeObserver' in window)) return;
      const target = containerRef.current;
      let resizeTimeout;

      const observer = new ResizeObserver((entries) => {
        // ResizeObserver 루프 방지를 위한 디바운싱
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const entry = entries && entries[0];
          const nextWidth = entry?.contentRect?.width || target.offsetWidth;
          // 불필요한 렌더링 방지를 위해 변경 시에만 갱신
          setContainerWidth((prev) => {
            const roundedPrev = Math.round(prev || 0);
            const roundedNext = Math.round(nextWidth || 0);
            // 5픽셀 이상 차이날 때만 업데이트 (미세한 변화 무시)
            return Math.abs(roundedPrev - roundedNext) > 5 ? roundedNext : prev;
          });
        }, 50); // 50ms 디바운싱
      });
      observer.observe(target);
      return () => {
        clearTimeout(resizeTimeout);
        try { observer.disconnect(); } catch (_) {}
      };
    } catch (_) {}
  }, []);

  // 실제 컨텐츠 높이 계산
  const calculateContentHeight = useMemo(() => {
    if (!validComponentsArray || validComponentsArray.length === 0) return 'auto';
    
    const maxY = Math.max(...validComponentsArray.map(comp => 
      (comp.position?.y || 0) + (comp.position?.h || 2)
    ));
    
    const rowHeight = calculateRowHeight(dashboardTemplate, containerWidth);
    const gridSpacing = dashboardTemplate?.dashboard_options?.grid_spacing ?? 10;
    
    // 충분한 여분 공간 추가 (100px)
    return (maxY * (rowHeight + gridSpacing)) + 100;
  }, [validComponentsArray, dashboardTemplate, containerWidth]);

  // 컬럼 수를 먼저 계산 (key prop에서 사용하기 위해)
  const columnCount = dashboardTemplate?.layout?.columns || 12;

  // 레이아웃 아이템 빠른 조회를 위한 맵 구성 (O(n))
  const layoutMap = useMemo(() => {
    const map = new Map();
    const lg = (layouts && layouts.lg) ? layouts.lg : [];
    for (const item of lg) {
      if (item && item.i) map.set(item.i, item);
    }
    return map;
  }, [layouts && layouts.lg]);

  // 그리드 설정을 메모이제이션 - 의존성 배열 최적화
  const gridConfig = useMemo(() => {
    const gridSpacing = dashboardTemplate?.dashboard_options?.grid_spacing;
    // getEffectiveCompactType 함수 사용으로 드래그 중 compaction 제어
    const effectiveCompactType = getEffectiveCompactType ? getEffectiveCompactType() : compactType;
    // preventCollision과 allowOverlap 설정
    // React Grid Layout에서 allowOverlap이 true이면 compaction이 작동하지 않음
    // compactType이 null이거나 편집모드에서 'none'을 선택한 경우에만 overlap 허용
    const preventCollisionValue = false; // 차트들이 서로 밀릴 수 있게 함
    const allowOverlapValue = effectiveCompactType === null || effectiveCompactType === 'none';
    // react-grid-layout deprecations: use only compactType
    const normalizedCompactType = (effectiveCompactType === 'none') ? null : effectiveCompactType;

    // 레이아웃 변경 핸들러 - 스마트 디바운싱
    const layoutChangeHandler = (() => {
      let timeoutId;

      // 레이아웃이 실제로 변경되었는지 확인하는 헬퍼 함수
      const hasLayoutChanged = (newLayout, oldLayout) => {
        if (!oldLayout || newLayout.length !== oldLayout.length) return true;

        return newLayout.some(newItem => {
          const oldItem = oldLayout.find(old => old.i === newItem.i);
          if (!oldItem) return true;

          // 위치나 크기가 변경되었는지 확인
          return (
            oldItem.x !== newItem.x ||
            oldItem.y !== newItem.y ||
            oldItem.w !== newItem.w ||
            oldItem.h !== newItem.h
          );
        });
      };

      return (layout) => {
        layoutChangeCountRef.current++;

        // 레이아웃이 실제로 변경되었는지 확인
        const layoutChanged = hasLayoutChanged(layout, lastLayoutRef.current);

        // 레이아웃이 안정화되었는지 판단
        // - 첫 2번의 호출은 초기화로 간주
        // - 레이아웃이 변경되지 않으면 안정화된 것으로 판단
        if (layoutChangeCountRef.current <= 2 || !layoutChanged) {
          if (!layoutChanged && layoutChangeCountRef.current > 2) {
            isStableRef.current = true;
          }
          lastLayoutRef.current = JSON.parse(JSON.stringify(layout));
          handleLayoutChange(layout);
          return;
        }

        // 안정화된 후에는 드래그/리사이즈 중에만 디바운싱 적용
        if (isStableRef.current && isDragging) {
          clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            lastLayoutRef.current = JSON.parse(JSON.stringify(layout));
            handleLayoutChange(layout);
          }, 100);
        } else {
          // 안정화되지 않았거나 드래그 중이 아니면 즉시 처리
          lastLayoutRef.current = JSON.parse(JSON.stringify(layout));
          handleLayoutChange(layout);
        }
      };
    })();

    return {
      className: `layout ${editMode ? 'edit-mode' : 'view-mode'}`,
      // 배경 스타일 제거 - 별도 오버레이에서 처리
      style: null,
      layouts,
      breakpoints: { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 },
      cols: {
        lg: columnCount, // 템플릿 설정 반영
        md: columnCount,
        sm: columnCount,
        xs: columnCount,
        xxs: columnCount
      },
      // ===== rowHeight 설정 (팝업 자동 크기 모드 지원) =====
      // 우선순위:
      //   1순위: overrideRowHeight (팝업 자동 크기 모드에서 전달됨)
      //   2순위: calculateRowHeight() (기본 계산 로직)
      // 
      // 팝업 자동 크기 모드일 때:
      //   - overrideRowHeight = colWidth (정사각형 그리드)
      //   - 결과: 1칸이 정사각형이 되어 차트 비율이 정확히 유지됨
      rowHeight: overrideRowHeight || calculateRowHeight(dashboardTemplate, containerWidth),
      // 🔧 무제한 행 수 허용 (정사각형 그리드)
      maxRows: Infinity,
      onLayoutChange: layoutChangeHandler,
      onBreakpointChange: handleBreakpointChange,
      // 🔧 드래그 이벤트 핸들러에 상태 추적 로직 및 스크롤 허용 추가
      onDragStart: (layout, oldItem, newItem, placeholder, e, element) => {
        setIsDragging(true);
        setParentIsDragging?.(true); // 부모 컴포넌트에 드래그 상태 전달
        setDraggedComponentId(oldItem.i);
        handleDragStart(layout, oldItem, newItem, placeholder, e, element);
      },
      onDrag: (layout, oldItem, newItem, placeholder, e, element) => {
        // 자동 스크롤 처리 - 성능 최적화를 위해 RAF 사용
        if (e && e.clientY) {
          requestAnimationFrame(() => handleAutoScroll(e.clientY));
        }
        handleDrag(layout, oldItem, newItem, placeholder, e, element);
      },
      onDragStop: (layout, oldItem, newItem, placeholder, e, element) => {
        setIsDragging(false);
        setParentIsDragging?.(false); // 부모 컴포넌트에 드래그 상태 전달
        setDraggedComponentId(null);
        // 자동 스크롤 중지
        stopAutoScroll();
        handleDragStop(layout, oldItem, newItem, placeholder, e, element);
      },
      onResizeStart: handleResizeStart,
      onResize: handleResize,
      onResizeStop: handleResizeStop,
      // draggableHandle 제거 - 전체 컴포넌트에서 드래그 가능
      // Ctrl/Cmd/Shift 키가 눌려있을 때는 드래그 비활성화 (다중 선택 모드)
      isDraggable: editMode && !isCtrlPressed && !isShiftPressed,
      isResizable: editMode && !isCtrlPressed && !isShiftPressed,
      // getEffectiveCompactType 결과를 표준 API에 맞게 전달
      compactType: normalizedCompactType,
      // 🔧 차트들이 서로 밀릴 수 있도록 preventCollision 항상 false
      preventCollision: false,
      allowOverlap: allowOverlapValue,
      // verticalCompact/horizontalCompact는 deprecated → 제거
      // 🔧 자동 높이 조정 활성화 (컨텐츠에 맞춰 확장)
      autoSize: true,
      // 🔧 CSS 변환 및 애니메이션 최적화
      useCSSTransforms: true, // GPU 가속 활용
      transformScale: 1,
      // 🔧 드래그 성능 최적화
      margin: [gridSpacing ?? 10, gridSpacing ?? 10],
      containerPadding: [0, 0],
      draggableCancel: ".react-resizable-handle",
      // 드래그 성능 최적화
      dragAllowanceX: 5, // 최소 드래그 거리 설정으로 의도치 않은 드래그 방지
      dragAllowanceY: 5  // 최소 드래그 거리 설정으로 의도치 않은 드래그 방지
    };
  }, [
    editMode,
    dashboardTemplate,
    columnCount,
    layouts,
    handleLayoutChange,
    handleBreakpointChange,
    handleDragStart,
    handleDrag,
    handleDragStop,
    handleResizeStart,
    handleResize,
    handleResizeStop,
    containerWidth,
    isUndoRedoOperation,
    setDraggedComponentId,
    handleAutoScroll,
    stopAutoScroll,
    setParentIsDragging,
    validComponentsArray,
    getEffectiveCompactType,
    isCtrlPressed,
    isShiftPressed,
    isDragging,
    overrideRowHeight
  ]);

  // 메뉴 닫기 함수 (useCallback 내부에서 사용하기 위해 먼저 선언)
  const closeContextMenu = useCallback(() => setContextMenu(prev => ({ ...prev, visible: false })), []);

  // z-순서 액션 핸들러
  const handleZOrderAction = useCallback((action, targetId) => {
    if (window.onZOrderAction) {
      window.onZOrderAction(targetId, action);
    }
  }, []);

  // 컨텍스트 메뉴 항목 생성 (그룹 + z-순서)
  const getContextMenuItems = useCallback((targetComponentId) => {
    const items = [];
    
    if (!targetComponentId) return items;

    // 그룹 관련 메뉴
    if (onCreateGroup && onUngroupComponents) {
      const groupId = getComponentGroup ? getComponentGroup(targetComponentId) : null;
      const isInGroup = !!groupId;
      const multipleSelected = selectedComponentIds && selectedComponentIds.length >= 2;
      
      if (isInGroup) {
        // 그룹에 속한 경우: 그룹 해제
        items.push({
          label: '그룹 해제',
          onClick: () => {
            closeContextMenu();
            onUngroupComponents(groupId);
          }
        });
        
        // 그룹 잠금/해제
        if (onToggleGroupLock) {
          const group = dashboardTemplate?.groups?.find(g => g.id === groupId);
          items.push({
            label: group?.locked ? '그룹 잠금 해제' : '그룹 잠금',
            onClick: () => {
              closeContextMenu();
              onToggleGroupLock(groupId);
            }
          });
        }
        
        // 그룹 내 독립선택 토글
        if (onToggleGroupIsolation) {
          const group = dashboardTemplate?.groups?.find(g => g.id === groupId);
          items.push({
            label: group?.isolatedSelection ? '✓ 그룹 내 독립선택' : '그룹 내 독립선택',
            onClick: () => {
              closeContextMenu();
              onToggleGroupIsolation(groupId);
            }
          });
        }
        
        // 그룹 전체 선택
        if (onSelectGroupComponents) {
          items.push({
            label: '그룹 전체 선택',
            onClick: () => {
              closeContextMenu();
              onSelectGroupComponents(groupId);
            }
          });
        }
        
        items.push({ label: '---', disabled: true }); // 구분선
      } else if (multipleSelected) {
        // 여러 개 선택되어 있고 그룹이 아닌 경우: 그룹 만들기
        items.push({
          label: `그룹 만들기 (${selectedComponentIds.length}개)`,
          onClick: () => {
            closeContextMenu();
            onCreateGroup();
          }
        });
        items.push({ label: '---', disabled: true }); // 구분선
      }
    }

    // // 개별 차트 독립선택 토글 (그룹이 아닌 경우에도 표시)
    // if (onToggleChartIsolation) {
    //   const component = dashboardTemplate?.components?.find(c => c.id === targetComponentId);
    //   if (component) {
    //     items.push({
    //       label: component.isolatedSelection ? '✓ 차트 독립선택' : '차트 독립선택',
    //       onClick: () => {
    //         closeContextMenu();
    //         onToggleChartIsolation(targetComponentId);
    //       }
    //     });
    //     items.push({ label: '---', disabled: true }); // 구분선
    //   }
    // }

    // Z-순서 메뉴
    items.push(
      { label: '맨 앞으로 보내기', onClick: () => { closeContextMenu(); handleZOrderAction('bringToFront', targetComponentId); } },
      { label: '맨 뒤로 보내기', onClick: () => { closeContextMenu(); handleZOrderAction('sendToBack', targetComponentId); } },
      { label: '앞으로 보내기', onClick: () => { closeContextMenu(); handleZOrderAction('bringForward', targetComponentId); } },
      { label: '뒤로 보내기', onClick: () => { closeContextMenu(); handleZOrderAction('sendBackward', targetComponentId); } }
    );

    return items;
  }, [
    selectedComponentIds,
    getComponentGroup,
    onCreateGroup,
    onUngroupComponents,
    onToggleGroupLock,
    onToggleGroupIsolation,
    onToggleChartIsolation,
    onSelectGroupComponents,
    dashboardTemplate,
    closeContextMenu,
    handleZOrderAction
  ]);

  // 컴포넌트가 없는 경우 빈 상태 표시
  if (!editMode && (!validComponentsArray || validComponentsArray.length === 0)) {
    return (
      <div className="dashboard-empty-components">
        <p>대시보드에 컴포넌트가 없습니다.</p>
        <p>우측 하단의 <span className="floating-edit-btn-inline">✎</span> 버튼을 클릭하여 편집을 시작하세요.</p>
      </div>
    );
  }

  // 컨텍스트 메뉴 상태 관리

  // 우클릭 핸들러 (grid item용)
  const handleContextMenu = (e, componentId) => {
    e.preventDefault();
    e.stopPropagation();
    setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetComponentId: componentId });
  };

  // 대시보드 배경 클릭 시 컨텍스트 메뉴 닫기
  const handleBackgroundMouseDown = (e) => {
    if (!editMode) return;
    if (!contextMenu?.visible) return;
    // 어떤 컴포넌트 영역도 아니라면 닫기
    const isOnComponent = e.target.closest('[data-component-id]');
    if (!isOnComponent) {
      closeContextMenu();
    }
  };

  return (
    <div 
      ref={containerRef}
      className="dashboard-grid-layout" 
      onMouseDown={editMode ? handleBackgroundMouseDown : undefined}
      style={{
        backgroundColor: 'transparent',
        position: 'relative',
        minHeight: typeof calculateContentHeight === 'number' ? `${calculateContentHeight}px` : calculateContentHeight
      }}
    >
      {/* 편집 모드일 때 교차점 그리드 표시 */}
      {editMode && (
        <div 
          className="grid-dots-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 0,
            // CSS 배경을 사용한 경량 도트 격자
            backgroundImage: (() => {
              const columns = dashboardTemplate?.layout?.columns || 12;
              const rh = calculateRowHeight(dashboardTemplate, containerWidth);
              const spacing = dashboardTemplate?.dashboard_options?.grid_spacing ?? 10;
              const cellH = Math.max(8, Math.round(rh + spacing));
              const safeWidth = Math.max(8, Math.round(containerWidth || 0));
              const cellW = Math.max(8, Math.round(safeWidth / Math.max(columns, 1)));
              const dotColor = columns > 48 ? 'rgba(100, 100, 100, 0.4)' : 'rgba(100, 100, 100, 0.8)';
              const dotSize = columns > 48 ? 1 : 2; // 반지름 비슷한 효과
              // radial-gradient 기반 도트 패턴
              return `radial-gradient(${dotColor} ${dotSize}px, transparent ${dotSize}px)`;
            })(),
            backgroundSize: (() => {
              const columns = dashboardTemplate?.layout?.columns || 12;
              const rh = calculateRowHeight(dashboardTemplate, containerWidth);
              const spacing = dashboardTemplate?.dashboard_options?.grid_spacing ?? 10;
              const cellH = Math.max(8, Math.round(rh + spacing));
              const safeWidth = Math.max(8, Math.round(containerWidth || 0));
              const cellW = Math.max(8, Math.round(safeWidth / Math.max(columns, 1)));
              return `${cellW}px ${cellH}px`;
            })()
          }}
        />
      )}

      <ResponsiveGridLayout
        key={`grid-${columnCount}-${editMode ? 'edit' : 'view'}`} // 컬럼 수와 모드 변경 시에만 리마운트
        {...gridConfig}>
        {validComponentsArray && validComponentsArray.map(component => {
          // 안전한 data-grid 속성 생성
          const layoutItem = layoutMap.get(component.id);
          
          // 레이아웃 아이템 디버깅
          if (component.id === 'chart_pie_1756782358023' || component.id.includes('pie')) {
          }
          const isLayoutLocked = component.layout_locked === true;
          
          // 위치고정된 컴포넌트의 static 속성 결정
          // - 위치고정 + 편집모드: false (편집 가능)
          // - 위치고정 + 일반모드: true (완전 고정)
          // - 일반 컴포넌트: false (React Grid Layout 전역 설정 따름)
          const isStatic = isLayoutLocked && !editMode;
          
          const dataGrid = layoutItem ? {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
            minW: layoutItem.minW || 1,
            minH: Math.min(layoutItem.minH || 1, layoutItem.h || 1),
            static: isStatic
          } : {
            x: component.position?.x || 0,
            y: component.position?.y || 0,
            w: component.position?.w || 3,
            h: component.position?.h || 2,
            minW: 1,
            minH: Math.min(1, component.position?.h || 2),
            static: isStatic
          };
          
          // 프레임 없는 차트인지 확인 (대소문자 무관)
          const isBadgeChart = component.chart_options?.viz_type?.toLowerCase() === 'badge';
          const defaultShowFrame = !isBadgeChart;
          const originalShowFrame = component.chart_options?.showFrame !== undefined ? component.chart_options.showFrame : defaultShowFrame;
          const gridItemClassName = (!editMode && !originalShowFrame) ? 'frameless-grid-item' : '';
          
          const isActive = !!activeChartState?.[component.id];
          const show = contextMenu.visible && contextMenu.targetComponentId === component.id;
          return (
            <div key={component.id} data-grid={dataGrid} data-component-id={component.id} className={gridItemClassName}
              onContextMenu={editMode ? e => handleContextMenu(e, component.id) : undefined}
            >
              <DashboardComponentRenderer
                key={`${component.id}-${contextMenu.visible ? contextMenu.targetComponentId : 'none'}`}
                component={component}
                dashboardTemplate={dashboardTemplate}
                componentData={chartsData[component.id]}
                editMode={editMode}
                isDragging={isDragging}
                draggedComponentId={draggedComponentId}
                resizeCounter={componentResizeCounters[component.id] || 0}
                onComponentDoubleClick={handleComponentDoubleClick}
                onDeleteComponent={deleteComponent}
                onRefreshChartData={refreshChartData}
                onRefreshAllCharts={refreshAllChartsData}
                textLabelPreviewData={textLabelPreviewData}
                onChartClick={onChartClick}
                isActiveChart={isActive}
                isClicked={clickedChartIds && clickedChartIds.includes(component.id)}
                activeQuery={activeChartState?.[component.id]}
                isSelected={isComponentSelected && isComponentSelected(component.id)}
                onComponentSelect={onComponentSelect}
                onComponentClick={onComponentClick}
                showContextMenu={show}
                contextMenuX={contextMenu.x}
                contextMenuY={contextMenu.y}
                zOrderMenuItems={show ? getContextMenuItems(component.id) : []}
                closeContextMenu={closeContextMenu}
                // chart div에도 onContextMenu 전달
                onChartContextMenu={editMode ? e => {
                  e.preventDefault();
                  e.stopPropagation();
                  setContextMenu({ visible: true, x: e.clientX, y: e.clientY, targetComponentId: component.id });
                } : undefined}
                currentPopupId={currentPopupId}
              />
            </div>
          );
        })}
      </ResponsiveGridLayout>
      
      {/* 그룹 바운딩 박스들 렌더링 (편집 모드에서만) */}
      {editMode && dashboardTemplate?.groups && layouts?.lg && (
        <>
          {getGroups(dashboardTemplate).map(group => {
            // 그룹에 속한 컴포넌트가 있는지 확인
            const groupComponentIds = getGroupComponentIds(dashboardTemplate, group.id);
            if (groupComponentIds.length === 0) return null;
            
            // 그룹의 컴포넌트 중 하나라도 선택되어 있는지 확인
            const isGroupSelected = selectedComponentIds && groupComponentIds.some(id => 
              selectedComponentIds.includes(id)
            );
            
            return (
              <GroupBoundingBox
                key={group.id}
                group={group}
                dashboardTemplate={dashboardTemplate}
                layout={layouts.lg}
                containerWidth={containerWidth}
                isSelected={isGroupSelected}
                selectedComponentIds={selectedComponentIds}
                onClick={() => {
                  // 그룹 라벨 클릭 시 전체 그룹 선택
                  if (onSelectGroupComponents) {
                    onSelectGroupComponents(group.id);
                  }
                }}
                onGroupResize={onGroupResize}
                onSelectGroupComponents={onSelectGroupComponents}
                onGroupDrag={onGroupDrag}
                onContextMenu={(e, groupId) => {
                  // 그룹의 첫 번째 컴포넌트 ID를 사용하여 컨텍스트 메뉴 표시
                  const groupComponentIds = getGroupComponentIds(dashboardTemplate, groupId);
                  if (groupComponentIds.length > 0) {
                    setContextMenu({ 
                      visible: true, 
                      x: e.clientX, 
                      y: e.clientY, 
                      targetComponentId: groupComponentIds[0] // 그룹의 첫 번째 컴포넌트 ID 사용
                    });
                  }
                }}
              />
            );
          })}
        </>
      )}
      
      {/* 컨텍스트 메뉴를 각 차트별로가 아니라, grid 전체에서 렌더링해도 됨(여기선 각 차트별로 전달) */}
    </div>
  );
});

DashboardGrid.displayName = 'DashboardGrid';

export default DashboardGrid; 